import { Component, OnInit, input, output, signal, computed, ChangeDetectionStrategy } from '@angular/core';

import { ReactiveFormsModule, FormGroup, FormBuilder } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconLabelComponent } from '../icon-label/icon-label.component';

/** The two infoscreen text fields that can be copied to the other booking periods. */
export type InfoscreenField = 'screenText' | 'level';

/**
 * FormInfoscreenComponent
 *
 * Form component for inputting infoscreen display information.
 * Displays two fields for:
 * - Skærmtekst (Screen text) - Organization name
 * - Infoskærmstekst - Generic context info for the infoscreen
 *   (e.g. competition level for matches, meeting type for meetings).
 *   Bound to the underlying `level` form control / `SkillLevel` DTO field.
 */
@Component({
  selector: 'app-form-infoscreen',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    IconLabelComponent,
  ],
  templateUrl: './form-infoscreen.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './form-infoscreen.component.scss',
})
export class FormInfoscreenComponent implements OnInit {
  private fb = new FormBuilder();

  // Input signals
  screenText = input<string>('');
  level = input<string>('');
  /** When true (more than one booking period), a copy icon appears once a field is edited. */
  canCopy = input<boolean>(false);

  // Output events
  screenTextChange = output<string>();
  levelChange = output<string>();
  /** Request to copy a field's value out to the other booking periods. */
  copyRequest = output<{ field: InfoscreenField; value: string }>();

  // Form
  form!: FormGroup;

  // Which fields have been edited — the copy icon shows only after an edit.
  private readonly edited = signal<Record<InfoscreenField, boolean>>({ screenText: false, level: false });
  readonly showCopyScreenText = computed(() => this.canCopy() && this.edited().screenText);
  readonly showCopyLevel = computed(() => this.canCopy() && this.edited().level);

  // Validation signal
  private formValid = signal(false);
  readonly isFormValid = computed(() => this.formValid());

  ngOnInit() {
    this.initializeForm();
  }

  private initializeForm() {
    this.form = this.fb.group({
      screenText: [this.screenText()],
      level: [this.level()],
    });
    // Subscribe to form status changes
    this.form.statusChanges.subscribe(() => {
      this.formValid.set(this.form.valid);
    });
    // Set initial validation state
    this.formValid.set(this.form.valid);
  }

  /** Mark a field as edited so its copy icon appears (fires live, on each keystroke). */
  onFieldInput(field: InfoscreenField) {
    if (!this.edited()[field]) {
      this.edited.update((e) => ({ ...e, [field]: true }));
    }
  }

  /**
   * Handle screen text change
   */
  onScreenTextChange(value: string) {
    this.screenTextChange.emit(value);
  }

  /**
   * Handle level change
   */
  onLevelChange(value: string) {
    this.levelChange.emit(value);
  }

  /** Ask the parent to copy this field's current value to the other booking periods. */
  requestCopy(field: InfoscreenField) {
    this.copyRequest.emit({ field, value: this.form.get(field)?.value ?? '' });
  }

  /** Hide the copy icon again after the value has been copied out. */
  clearEdited(field: InfoscreenField) {
    if (this.edited()[field]) {
      this.edited.update((e) => ({ ...e, [field]: false }));
    }
  }

  /** Overwrite a field's value (used when another period copies its text here). */
  setField(field: InfoscreenField, value: string) {
    const control = this.form.get(field);
    if (!control) return;
    control.setValue(value);
    control.markAsDirty();
  }
}
